import {
  CellReviewProvider,
  FIELD_COMMENT_MAX_LENGTH,
} from "@platform/plan-grid-core";

import { useRatesErrorHandler } from "../context/RatesErrorContext";
import { useBuildRateCellReviews } from "../hooks/useBuildRateCellReviews";

import type { AiRateFieldsMeta, BenefitPlanGroup } from "../types/models";
import type { AxiosInstance } from "axios";
import type { FC, ReactNode } from "react";

/** Broker highlights are a Quotes-only notation, so the Build tab never shows one. */
const noHighlight = (): boolean => false;
const ignore = (): void => {};

export interface BuildRateCellReviewProviderProps {
  /** The groups whose plans carry the embedded field comments to index. */
  benefitPlanGroups: BenefitPlanGroup[];
  axiosInstance: AxiosInstance;
  /** BTX RFP the plans belong to. */
  rfpId: string;
  /** AI extraction hints per plan; only plans with one take the "Edited" mark. */
  aiFieldsByPlanId?: Record<string, AiRateFieldsMeta>;
  /** Overrides the grid's own error handler, which is used by default. */
  onSaveError?: (message: string) => void;
  children: ReactNode;
}

/**
 * Backs rate cell reviews and "Edited" marks with BTX field comments, for the
 * Build tab.
 *
 * The Quotes tab mounts `CellReviewProvider` itself against MPX cell reviews,
 * because its columns are proposal plans and BTX field comments are foreign-keyed
 * to benefit plans. Both surfaces reach the rates grid through the same context.
 */
export const BuildRateCellReviewProvider: FC<
  BuildRateCellReviewProviderProps
> = ({
  benefitPlanGroups,
  axiosInstance,
  rfpId,
  aiFieldsByPlanId,
  onSaveError,
  children,
}) => {
  const reportRatesError = useRatesErrorHandler();
  const {
    getReview,
    upsertReview,
    clearReview,
    setValidated,
    setComment,
    getEdited,
    setEdited,
  } = useBuildRateCellReviews({
    benefitPlanGroups,
    axiosInstance,
    rfpId,
    aiFieldsByPlanId,
    onSaveError: onSaveError ?? reportRatesError,
  });

  return (
    <CellReviewProvider
      getReview={getReview}
      upsertReview={upsertReview}
      clearReview={clearReview}
      setValidated={setValidated}
      setComment={setComment}
      getBrokerHighlight={noHighlight}
      setBrokerHighlight={ignore}
      clearBrokerHighlight={ignore}
      getEdited={getEdited}
      setEdited={setEdited}
      commentMaxLength={FIELD_COMMENT_MAX_LENGTH}
    >
      {children}
    </CellReviewProvider>
  );
};
